import { getApps, initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { authService } from "@src/@api-services/auth";
import {
  firebaseConfig,
  FIREBASE_MESSAGING_SW_PATH,
  FIREBASE_VAPID_KEY,
} from "@configs/firebase";

const FCM_DEVICE_ID_KEY = "quila_fcm_device_id";

let foregroundReady = false;
let tokenSyncInFlight: Promise<void> | null = null;

const getFirebaseApp = () =>
  getApps().length > 0 ? getApps()[0]! : initializeApp(firebaseConfig);

const getOrCreateDeviceId = (): string => {
  let deviceId = localStorage.getItem(FCM_DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(FCM_DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

const getClickUrl = (payload: {
  fcmOptions?: { link?: string };
  data?: Record<string, string>;
}): string =>
  payload.data?.url || payload.fcmOptions?.link || payload.data?.link || "";

const getTitle = (payload: {
  notification?: { title?: string };
  data?: Record<string, string>;
}): string =>
  payload.notification?.title || payload.data?.title || "Notification";

const getBody = (payload: {
  notification?: { body?: string };
  data?: Record<string, string>;
}): string =>
  payload.notification?.body || payload.data?.body || "";

const showPushNotification = (
  payload: {
    notification?: { title?: string; body?: string };
    fcmOptions?: { link?: string };
    data?: Record<string, string>;
  }
): void => {
  if (Notification.permission !== "granted") {
    return;
  }

  const url = getClickUrl(payload);
  const title = getTitle(payload);
  const body = getBody(payload);

  const notification = new Notification(title, {
    body,
    tag: "quila-fcm",
    icon: payload?.data?.logo,
    data: { url },
  });

  notification.onclick = () => {
    notification.close();
    window.focus();
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };
};

const getMessagingSwRegistration = async (): Promise<ServiceWorkerRegistration> => {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) {
    return existing;
  }

  const registration = await navigator.serviceWorker.register(
    FIREBASE_MESSAGING_SW_PATH,
    { scope: "/", type: "classic" }
  );
  await navigator.serviceWorker.ready;
  return registration;
};

/** Register SW + onMessage — per Firebase receive-messages foreground guide */
export const initForegroundMessaging = async (): Promise<void> => {
  if (foregroundReady) {
    return;
  }

  if (!(await isSupported())) {
    console.warn("[FCM] Not supported in this browser");
    return;
  }

  if (!("serviceWorker" in navigator)) {
    return;
  }

  await getMessagingSwRegistration();

  const messaging = getMessaging(getFirebaseApp());

  onMessage(messaging, (payload) => {
    console.log("Message received.", payload);
    showPushNotification(payload);
  });

  foregroundReady = true;
};

/** getToken + save to backend so push channel delivery works */
export const syncFcmDeviceToken = async (): Promise<void> => {
  if (!authService.isAuthenticated()) {
    return;
  }

  if (tokenSyncInFlight) {
    return tokenSyncInFlight;
  }

  tokenSyncInFlight = (async () => {
    if (!(await isSupported())) {
      return;
    }

    if (!FIREBASE_VAPID_KEY) {
      console.warn("[FCM] Missing VITE_FIREBASE_VAPID_KEY in .env");
      return;
    }

    await initForegroundMessaging();

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("[FCM] Notification permission not granted");
      return;
    }

    const registration = await getMessagingSwRegistration();
    const messaging = getMessaging(getFirebaseApp());

    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) {
      console.warn("[FCM] No registration token available");
      return;
    }

    console.log("[FCM] Registration token:", token);

    await authService.registerDeviceToken(token, getOrCreateDeviceId());
    console.log("[FCM] Device token saved");
  })()
    .catch((error) => {
      console.error("[FCM] Token sync failed:", error);
    })
    .finally(() => {
      tokenSyncInFlight = null;
    });

  return tokenSyncInFlight;
};

export { firebaseConfig, FIREBASE_MESSAGING_SW_PATH };
